<script setup>
import { ref } from 'vue';

const periodOptions = [
  { value: 'today', label: "Aujourd'hui" },
  { value: 'week', label: 'Cette semaine' },
  { value: 'month', label: 'Ce mois' },
  { value: 'year', label: 'Cette année' }
];

const selectedPeriod = ref('month');

const stats = ref({
  clientBalance: 0,
  totalTransactions: 0,
  totalCustomers: 0,
  netProfit: 0,
  successfulTransactions: 0,
  failedTransactions: 0
});
</script>

<template>
  <PageLayout>
    <template #headerTitle>Dashboard</template>

    <div class="px-4 sm:px-6 lg:px-8 mt-4 sm:mt-6 space-y-4 sm:space-y-6">
      <!-- FIRST ROW - Client Balance & Total Transactions -->
      <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
        <!-- Client Balance -->
        <div class="bg-slate-100 rounded-2xl sm:rounded-3xl p-4 sm:p-6">
          <div class="flex items-center justify-between gap-3">
            <div class="flex-1 min-w-0">
              <p class="text-xs sm:text-sm text-gray-500 font-medium">
                Solde des clients
              </p>
              <p
                class="text-xl sm:text-2xl md:text-3xl font-bold mt-1 sm:mt-2 truncate"
              >
                {{ stats.clientBalance?.toLocaleString() || '...' }} CFA
              </p>
            </div>
            <div
              class="w-12 h-12 sm:w-14 sm:h-14 text-primary-500 bg-primary-100 rounded-xl sm:rounded-2xl flex items-center justify-center flex-shrink-0"
            >
              <span class="w-full h-full p-3">
                <ColorImg
                  src="/images/all/money-1.svg"
                  alt=""
                  class="w-full h-full bg-current/70"
                />
              </span>
            </div>
          </div>
        </div>

        <!-- Total Transactions -->
        <div class="bg-slate-100 rounded-2xl sm:rounded-3xl p-4 sm:p-6">
          <div class="flex items-center justify-between gap-3">
            <div class="flex-1 min-w-0">
              <p class="text-xs sm:text-sm text-gray-500 font-medium">
                Total transactions
              </p>
              <p
                class="text-xl sm:text-2xl md:text-3xl font-bold mt-1 sm:mt-2 truncate"
              >
                {{ stats.totalTransactions?.toLocaleString() || '...' }}
              </p>
            </div>
            <div
              class="w-12 h-12 sm:w-14 sm:h-14 text-blue-500 bg-blue-100 rounded-xl sm:rounded-2xl flex items-center justify-center flex-shrink-0"
            >
              <span class="w-full h-full p-3">
                <ColorImg
                  src="/images/all/stats.svg"
                  alt=""
                  class="w-full h-full bg-current/70"
                />
              </span>
            </div>
          </div>
        </div>
        <!-- Total Transactions -->
        <div class="bg-slate-100 rounded-2xl sm:rounded-3xl p-4 sm:p-6">
          <div class="flex items-center justify-between gap-3">
            <div class="flex-1 min-w-0">
              <p class="text-xs sm:text-sm text-gray-500 font-medium">
                Total Clients
              </p>
              <p
                class="text-xl sm:text-2xl md:text-3xl font-bold mt-1 sm:mt-2 truncate"
              >
                {{ stats.totalCustomers?.toLocaleString() || '...' }}
              </p>
            </div>
            <div
              class="w-12 h-12 sm:w-14 sm:h-14 text-blue-500 bg-blue-100 rounded-xl sm:rounded-2xl flex items-center justify-center flex-shrink-0"
            >
              <span class="w-full h-full p-3">
                <ColorImg
                  src="/images/all/user.svg"
                  alt=""
                  class="w-full h-full bg-current/70"
                />
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- SECOND ROW - Net Profit, Successful, Failed with Period Selector -->
      <div class="bg-slate-100 rounded-2xl sm:rounded-3xl p-4 sm:p-6">
        <!-- Period Selector -->
        <div
          class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-6"
        >
          <h3 class="text-base sm:text-lg font-semibold">
            Statistiques de transactions
          </h3>
          <div class="flex flex-wrap gap-1.5 sm:gap-2 w-full sm:w-auto">
            <button
              v-for="option in periodOptions"
              :key="option.value"
              @click="selectedPeriod = option.value"
              :class="[
                'px-2 sm:px-4 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-medium transition-all text-center',
                selectedPeriod === option.value
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50 -sm'
              ]"
            >
              {{ option.label }}
            </button>
          </div>
        </div>

        <!-- Stats Grid -->
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6">
          <!-- Net Profit -->
          <div class="bg-white rounded-xl sm:rounded-2xl p-3 sm:p-5">
            <div class="flex items-center gap-2 sm:gap-3">
              <div
                class="w-10 h-10 sm:w-12 sm:h-12 text-green-500 bg-green-100 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0"
              >
                <span class="w-full h-full p-3">
                  <ColorImg
                    src="/images/all/stats.svg"
                    alt=""
                    class="w-full h-full bg-current/70"
                  />
                </span>
              </div>
              <div class="min-w-0 flex-1">
                <p class="text-xs sm:text-sm text-gray-500">Profit net</p>
                <p
                  class="text-lg sm:text-xl md:text-2xl font-bold text-green-600 truncate"
                >
                  {{ stats.netProfit?.toLocaleString() || '...' }} CFA
                </p>
              </div>
            </div>
          </div>

          <!-- Successful Transactions -->
          <div class="bg-white rounded-xl sm:rounded-2xl p-3 sm:p-5">
            <div class="flex items-center gap-2 sm:gap-3">
              <div
                class="w-10 h-10 sm:w-12 sm:h-12 text-emerald-500 bg-emerald-100 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0"
              >
                <span class="w-full h-full p-3">
                  <ColorImg
                    src="/images/all/check.svg"
                    alt=""
                    class="w-full h-full bg-current/70"
                  />
                </span>
              </div>
              <div class="min-w-0 flex-1">
                <p class="text-xs sm:text-sm text-gray-500">Réussies</p>
                <p
                  class="text-lg sm:text-xl md:text-2xl font-bold text-emerald-600 truncate"
                >
                  {{ stats.successfulTransactions?.toLocaleString() || '...' }}
                </p>
              </div>
            </div>
          </div>

          <!-- Failed Transactions -->
          <div class="bg-white rounded-xl sm:rounded-2xl p-3 sm:p-5">
            <div class="flex items-center gap-2 sm:gap-3">
              <div
                class="w-10 h-10 sm:w-12 sm:h-12 text-red-500 bg-red-100 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0"
              >
                <span class="w-full h-full p-3">
                  <ColorImg
                    src="/images/all/close.svg"
                    alt=""
                    class="w-full h-full bg-current/70"
                  />
                </span>
              </div>
              <div class="min-w-0 flex-1">
                <p class="text-xs sm:text-sm text-gray-500">Échouées</p>
                <p
                  class="text-lg sm:text-xl md:text-2xl font-bold text-red-600 truncate"
                >
                  {{ stats.failedTransactions?.toLocaleString() || '...' }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- LATEST TRANSACTIONS -->
      <div class="rounded-2xl sm:rounded-3xl bg-slate-100 p-3 sm:p-4">
        <div class="flex justify-between px-3 sm:px-4">
          <div class="font-bold text-sm sm:text-base">
            Transactions récentes
          </div>
          <router-link to="/history" class="text-sm sm:text-base"
            >Tout voir</router-link
          >
        </div>
        <div class="mt-3 sm:mt-4">
          <LatestTransactions />
        </div>
      </div>
    </div>
  </PageLayout>
</template>
